import { useState } from 'react';
import { z } from 'zod';
import { useAuth } from '@/hooks/use-auth';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  ModelList,
  ModelUpload,
  ModelSchemaViewer,
  ModelConfigurator,
} from '@/components/settings';
import { UserManagement } from '@/components/settings/UserManagement';
import { AgencySpreads } from '@/components/settings/AgencySpreads';
import {
  TierCatalog,
  PricingConfigEditor,
  BillingOverrides,
} from '@/components/admin/billing';
import { AllDealsAdmin } from '@/components/admin';

export const ADMIN_TABS = [
  'models',
  'users',
  'spreads',
  'billing',
  'deals',
] as const;
export type AdminTab = (typeof ADMIN_TABS)[number];

/**
 * Billing sub-tabs — signup-billing-mvp Phase 5 Session 6 (2026-05-13).
 * `?tab=billing&sub=overrides|tiers|pricing`; defaults to `overrides`.
 */
export const BILLING_SUB_TABS = ['overrides', 'tiers', 'pricing'] as const;
export type AdminBillingSubTab = (typeof BILLING_SUB_TABS)[number];

/**
 * URL search-param schema for `/admin`. Defaults missing/unknown `tab` to
 * `'models'` so the empty-state link from `/builder` (Session 11) deep-links
 * cleanly to `?tab=models`. `.catch('models')` swallows arbitrary garbage in
 * the URL without throwing.
 *
 * Phase 5 Session 6 (2026-05-13): adds optional `sub` for billing sub-tabs.
 */
export const adminSearchSchema = z.object({
  tab: z.enum(ADMIN_TABS).catch('models').default('models'),
  // `sub` is optional + catch-on-bad-value but does NOT default — the parse
  // result omits the field when absent so existing admin tab consumers see
  // `{tab: 'X'}` without surprise additions. The component resolves the
  // default ('overrides') at render time via `sub ?? 'overrides'`.
  sub: z.enum(BILLING_SUB_TABS).catch('overrides').optional(),
});

export function AdminPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { tab, sub } = useSearch({ from: '/admin' });
  const activeSub: AdminBillingSubTab = (sub ??
    'overrides') as AdminBillingSubTab;
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [configuringModel, setConfiguringModel] = useState<string | null>(null);

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-base text-text-muted font-mono mb-3">
            Access Denied
          </p>
          <p className="text-sm text-text-dim font-mono mb-4">
            You need admin privileges to view this page.
          </p>
          <button
            onClick={() => navigate({ to: '/cockpit' })}
            className="px-6 py-3 bg-accent-dim border border-accent/15 rounded-[3px] text-accent font-mono text-sm font-semibold cursor-pointer"
          >
            Go to Cockpit
          </button>
        </div>
      </div>
    );
  }

  return (
    // 2026-05-26: `space-y-4` (NOT `flex flex-col gap-4`) so admin tabs that
    // stack `<Panel>` children render the Panels at natural content height.
    // Panel is built `flex flex-col overflow-hidden min-h-0` with a
    // `flex-1 overflow-hidden min-h-0` body — inside a bounded-height flex-col
    // wrapper, each Panel pins itself to the available height and clips its
    // body internally, so `overflow-y-auto h-full` here never fires. Block-
    // flow stacking lets Panels grow naturally, then the wrapper scrolls when
    // total height exceeds the viewport. Users + Spreads tabs were the
    // primary victims (UserManagement's 19-row list was clipping to ~300px).
    <div className="p-5 space-y-4 overflow-y-auto h-full">
      <div className="flex items-center gap-4">
        <span className="text-xs font-mono text-accent font-semibold tracking-[1.4px]">
          ADMIN
        </span>
        <div className="flex items-center gap-1">
          {ADMIN_TABS.map((t) => (
            <button
              key={t}
              onClick={() => navigate({ to: '/admin', search: { tab: t } })}
              data-testid={`admin-tab-${t}`}
              className={`px-4 py-1.5 text-sm font-mono rounded cursor-pointer transition-colors ${
                tab === t
                  ? 'bg-accent-dim text-accent border border-accent/15'
                  : 'text-text-muted hover:text-text bg-transparent border border-transparent'
              }`}
            >
              {t === 'spreads'
                ? 'Market Spreads'
                : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {tab === 'users' && <UserManagement />}

      {tab === 'spreads' && <AgencySpreads />}

      {tab === 'deals' && <AllDealsAdmin />}

      {/*
        Phase 5 Session 6 (2026-05-13): billing tab now carries 3 sub-tabs
        switched via `?sub=overrides|tiers|pricing`. The sub-tab bar renders
        only when tab === 'billing' so the other admin tabs are unaffected.
      */}
      {tab === 'billing' && (
        // `space-y-3` for the same reason as the outer wrapper above —
        // billing sub-tab bodies (TierCatalog, BillingOverrides) need to
        // size to natural content so the outer wrapper can scroll them.
        <div className="space-y-3">
          <div className="flex items-center gap-1">
            {BILLING_SUB_TABS.map((s) => (
              <button
                key={s}
                onClick={() =>
                  navigate({
                    to: '/admin',
                    search: { tab: 'billing', sub: s },
                  })
                }
                data-testid={`admin-billing-sub-tab-${s}`}
                className={`px-3 py-1 text-xs font-mono rounded cursor-pointer transition-colors ${
                  activeSub === s
                    ? 'bg-accent-dim text-accent border border-accent/15'
                    : 'text-text-muted hover:text-text bg-transparent border border-transparent'
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          {activeSub === 'overrides' && <BillingOverrides />}
          {activeSub === 'tiers' && <TierCatalog />}
          {activeSub === 'pricing' && <PricingConfigEditor />}
        </div>
      )}

      {tab === 'models' && (
        // Wrap in a div with `space-y-4` so the Models tab's sibling blocks
        // (grid, schema viewer, configurator) keep their visual rhythm now
        // that the outer wrapper is block-flow (was `flex flex-col gap-4`).
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ModelUpload onIngested={(id) => setConfiguringModel(id)} />
            <ModelList
              onSelectModel={setSelectedModel}
              selectedModel={selectedModel}
              onConfigure={setConfiguringModel}
            />
          </div>

          {selectedModel && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono text-text-muted">
                  Viewing: {selectedModel}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setConfiguringModel(selectedModel)}
                    className="px-3 py-1.5 text-xs font-mono text-accent cursor-pointer bg-accent-dim border border-accent/15 rounded-[2px] hover:bg-accent/13 transition-colors"
                  >
                    Configure
                  </button>
                  <button
                    onClick={() => setSelectedModel(null)}
                    className="px-3 py-1.5 text-xs font-mono text-text-dim hover:text-text-muted cursor-pointer bg-input-bg border border-panel-border rounded-[2px]"
                  >
                    Close
                  </button>
                </div>
              </div>
              <ModelSchemaViewer modelId={selectedModel} />
            </div>
          )}

          {configuringModel && (
            <ModelConfigurator
              modelId={configuringModel}
              onClose={() => setConfiguringModel(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}
